import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Dropdown,
  ListGroup,
  Accordion
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaEye,
  FaCopy,
  FaSave,
  FaArrowUp,
  FaArrowDown,
  FaAngleDoubleUp,
  FaAngleDoubleDown,
  FaGripVertical,
  FaCog,
  FaLayerGroup,
  FaList,
  FaUsers,
  FaUserPlus,
  FaInfoCircle,
  FaCheckCircle
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import RoleAssignmentModal from './RoleAssignmentModal';
import RelatedListManager from './RelatedListManager';
import LayoutWidgetManager from './LayoutWidgetManager';
import ConfigureDetailLayoutActionsModal from './ConfigureDetailLayoutActionsModal';
import LayoutRecordViewRoleBulkPage from './LayoutRecordViewRoleBulkPage';
import './css/LayoutManager.css';
import { getEntitlementsSection, hasPackage } from '../../utils/entitlements';

/** Browser console: only for rejected layout field drag/drops (other LayoutManager logs removed for now). */
function logLayoutDropFailure(reason, details) {
  console.warn('[LayoutManager] Drop failed:', reason, details !== undefined ? details : '');
}

const LM_LAYOUT_DEBUG_LOG = false;
const lmLog = LM_LAYOUT_DEBUG_LOG ? (...args) => console.log(...args) : () => {};

const LayoutManager = ({ objectName, initialLayoutId, onInitialLayoutOpened }) => {
  const navigate = useNavigate();
  const [assignmentsModalOpen, setAssignmentsModalOpen] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [fields, setFields] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [showSimpleCreateModal, setShowSimpleCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [layoutForm, setLayoutForm] = useState({
    layout_name: '',
    layout_type: 'DETAIL',
    total_columns: 3, // Number of columns for the layout (1, 2, or 3)
    detail_column_position: 1, // Which column the detail layout appears in (1, 2, or 3)
    sections: []
  });
  const [simpleCreateForm, setSimpleCreateForm] = useState({
    layout_name: '',
    layout_type: 'DETAIL'
  });
  const [availableFields, setAvailableFields] = useState([]);
  const [showFieldConfigModal, setShowFieldConfigModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingFieldSectionIndex, setEditingFieldSectionIndex] = useState(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [isSectionDragging, setIsSectionDragging] = useState(false);
  const [showRelatedListManager, setShowRelatedListManager] = useState(false);
  const [selectedLayoutForRelatedLists, setSelectedLayoutForRelatedLists] = useState(null);
  const [showLayoutWidgetManager, setShowLayoutWidgetManager] = useState(false);
  const [selectedLayoutForWidgets, setSelectedLayoutForWidgets] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isCloning, setIsCloning] = useState(false);
  const [originalLayoutName, setOriginalLayoutName] = useState('');
  const [showCloneConfigModal, setShowCloneConfigModal] = useState(false);
  const [layoutToClone, setLayoutToClone] = useState(null);
  const [cloneConfigForm, setCloneConfigForm] = useState({
    layout_name: '',
    layout_type: 'DETAIL'
  });
  const [showActionsConfigModal, setShowActionsConfigModal] = useState(false);
  const [selectedLayoutForActions, setSelectedLayoutForActions] = useState(null);
  const [generateDocActions, setGenerateDocActions] = useState([]); // Array of { label, templateAction }
  const [showLayoutConfigModal, setShowLayoutConfigModal] = useState(false);
  const [standardObjectConfig, setStandardObjectConfig] = useState({});
  const [hiddenFields, setHiddenFields] = useState([]); // config.hidden_fields: not addable to layouts
  const deepLinkLayoutOpenedRef = useRef(null);

  const layoutConfig = getEntitlementsSection('layout', {});
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  // Same flag as ObjectManager / TabManager: company_subscription (+ license) objectManager.enable_standard
  const enableStandardObject =
    objectManagerConfig?.enable_standard === true ||
    objectManagerConfig?.enableStandard === true;
  const ctiEnabled = hasPackage('telecmi') || hasPackage('callyzer');
  const allowLayoutCreate =
    layoutConfig?.allowCreate ?? layoutConfig?.allow_create ?? true;
  const maxLayouts = Number(layoutConfig?.maxLayouts);
  const isStandardLayoutValue = (layout) =>
    layout && (layout.is_standard || layout.is_standard === true || layout.Is_Standard || layout.IS_STANDARD);
  const isEditLayoutType = (layout) =>
    (layout?.layout_type ?? layout?.Layout_Type ?? '')
      .toString()
      .toLowerCase() === 'edit';
  // Object Manager: hide EDIT layouts in the list (edit UI uses record-view-scoped layouts elsewhere).
  const visibleLayouts = layouts.filter((l) => !isEditLayoutType(l));
  const customLayoutCount = layouts.filter(layout => !isStandardLayoutValue(layout)).length;
  const isLayoutLimitReached = Number.isFinite(maxLayouts)
    && maxLayouts >= 0
    && customLayoutCount >= maxLayouts;
  const canCreateLayout = allowLayoutCreate && !isLayoutLimitReached;

  // Lock detail section when editing a DETAIL layout and standard_object has edit_detail_section: false
  const layoutType = (editingLayout?.layout_type || layoutForm?.layout_type || '').toString().toLowerCase();
  const isDetailLayout = layoutType === 'detail';
  const isDetailSectionLocked =
    !enableStandardObject &&
    isDetailLayout &&
    standardObjectConfig?.edit_detail_section === false;

  const isDetailSection = (section) => {
    const name = (section?.section_name || '').toString().toLowerCase();
    return name === 'detail' || name === 'details' || name === 'main';
  };

  // Detail section is locked for: (1) DETAIL layout with edit_detail_section false, OR (2) user object standard layout.
  // When objectManager.enable_standard is true (subscription / license), allow editing like custom layouts (rules still apply, e.g. required base fields).
  const isDetailSectionLockedForSection = (section) =>
    (isDetailSectionLocked && isDetailSection(section)) ||
    (!enableStandardObject &&
      objectName &&
      objectName.toLowerCase() === 'user' &&
      editingLayout &&
      isStandardLayoutValue(editingLayout) &&
      isDetailSection(section));

  useEffect(() => {
    if (objectName) {
      lmLog('LayoutManager - objectName changed, fetching data for:', objectName);
      setInitialLoading(true);
      fetchLayouts();
      fetchFields();
      fetchRoles();
      // Fetch standard_object config for this object (e.g. edit_detail_section)
      const fetchStandardObjectConfig = async () => {
        try {
          const api = new DataApi('objectconfig/standard-object-config');
          const response = await api.fetchById(objectName);
          if (response?.data?.success && response?.data?.data?.config) {
            setStandardObjectConfig(response.data.data.config);
          } else {
            setStandardObjectConfig({});
          }
        } catch (err) {
          console.warn('LayoutManager - Could not load standard object config:', err);
          setStandardObjectConfig({});
        }
      };
      fetchStandardObjectConfig();
    } else {
      setStandardObjectConfig({});
    }
  }, [objectName]);

  // Open layout editor when linked from bulk assignment page (?layoutId= on Object Manager URL).
  // handleEditLayout is intentionally omitted from deps: only initialLayoutId / layouts should retrigger.
  useEffect(() => {
    if (!initialLayoutId || initialLoading || !objectName || !layouts.length) {
      return;
    }
    const key = `${objectName}:${initialLayoutId}`;
    if (deepLinkLayoutOpenedRef.current === key) {
      return;
    }
    const layout = layouts.find(
      (l) => String(l.layout_id || l.id) === String(initialLayoutId)
    );
    if (!layout) {
      deepLinkLayoutOpenedRef.current = key;
      if (typeof onInitialLayoutOpened === 'function') {
        onInitialLayoutOpened();
      }
      return;
    }
    deepLinkLayoutOpenedRef.current = key;
    void (async () => {
      await handleEditLayout(layout);
      if (typeof onInitialLayoutOpened === 'function') {
        onInitialLayoutOpened();
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- open when layout list / deep link id changes; not when handleEditLayout identity changes
  }, [objectName, initialLayoutId, initialLoading, layouts, onInitialLayoutOpened]);

  useEffect(() => {
    deepLinkLayoutOpenedRef.current = null;
  }, [objectName]);

  // Debug effect to monitor sections changes
  useEffect(() => {
    lmLog('Sections state updated:', layoutForm.sections.map((s, i) => `${i}: ${s.section_name}`));
  }, [layoutForm.sections]);

  const deriveLayoutCounts = (sectionsPayload) => {
    const sectionsData = Array.isArray(sectionsPayload)
      ? sectionsPayload
      : (sectionsPayload?.sections || []);

    if (!Array.isArray(sectionsData) || sectionsData.length === 0) {
      return { sectionCount: 0, fieldCount: 0 };
    }

    const firstSection = sectionsData[0];

    // Nested sections shape: [{ section_id, fields: [...] }]
    if (Array.isArray(firstSection.fields)) {
      const fieldCount = sectionsData.reduce((count, section) => {
        return count + (Array.isArray(section.fields) ? section.fields.length : 0);
      }, 0);
      return { sectionCount: sectionsData.length, fieldCount };
    }

    // Row-based shape: one row per field with section_id/section_name
    const sectionIds = new Set(
      sectionsData
        .map(row => row.section_id || row.section_name || row.section_label)
        .filter(Boolean)
    );
    const fieldCount = sectionsData.filter(row => row.field_id || row.field_name).length;

    return { sectionCount: sectionIds.size, fieldCount };
  };

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      setError('');

      lmLog('Fetching layouts for object:', objectName);
      const api = new DataApi(`objectconfig/layouts/${objectName}`);
      const response = await api.fetchAll();

      lmLog('Layout API Response:', response);

      if (response?.data?.success) {
        const layoutsData = response.data.data || [];
        lmLog('Layouts found:', layoutsData.length);
        lmLog('Layout data sample:', layoutsData[0]);
        lmLog('🔍 All layouts data:', layoutsData.map(layout => ({
          id: layout.layout_id,
          name: layout.layout_name,
          is_standard: layout.is_standard,
          Is_Standard: layout.Is_Standard,
          IS_STANDARD: layout.IS_STANDARD,
          allKeys: Object.keys(layout)
        })));

        // Calculate counts for each layout
        const layoutsWithCounts = await Promise.all(layoutsData.map(async (layout) => {
          try {
            lmLog('Fetching sections for layout:', layout.layout_id);
            lmLog('Sections Count:', `objectconfig/layouts/${layout.layout_id}/sections`);
            // Fetch sections for this layout to get section count
            const sectionsApi = new DataApi(`objectconfig/layouts/${layout.layout_id}/sections`);
            const sectionsResponse = await sectionsApi.fetchAll();
            const sectionsPayload = sectionsResponse?.data?.success ? sectionsResponse.data.data : [];
            const { sectionCount, fieldCount } = deriveLayoutCounts(sectionsPayload);

            // For now, set role count to 0 (can be updated when roles are implemented)
            const roleCount = 0;

            // Parse actions if they exist
            let parsedActions = [];
            if (layout.actions) {
              if (typeof layout.actions === 'string') {
                try {
                  parsedActions = JSON.parse(layout.actions);
                } catch (e) {
                  console.warn('Failed to parse actions JSON:', e);
                  parsedActions = [];
                }
              } else if (Array.isArray(layout.actions)) {
                parsedActions = layout.actions;
              }
            }

            const processedLayout = {
              ...layout,
              section_count: sectionCount,
              field_count: fieldCount,
              role_count: roleCount,
              actions: parsedActions // Include parsed actions
            };
            lmLog('🔍 Processed layout:', {
              id: processedLayout.layout_id,
              name: processedLayout.layout_name,
              is_standard: processedLayout.is_standard,
              Is_Standard: processedLayout.Is_Standard,
              IS_STANDARD: processedLayout.IS_STANDARD
            });
            return processedLayout;
          } catch (error) {
            console.error(`Error calculating counts for layout ${layout.layout_id}:`, error);
            return {
              ...layout,
              section_count: 0,
              field_count: 0,
              role_count: 0
            };
          }
        }));

        // Sort by created date (oldest first, ascending); fallback to lastmodifieddate if createddate missing
        const sortByCreatedDate = (a, b) => {
          const dateA = a.createddate ? new Date(a.createddate).getTime() : (a.lastmodifieddate ? new Date(a.lastmodifieddate).getTime() : 0);
          const dateB = b.createddate ? new Date(b.createddate).getTime() : (b.lastmodifieddate ? new Date(b.lastmodifieddate).getTime() : 0);
          return dateA - dateB; // oldest first
        };
        layoutsWithCounts.sort(sortByCreatedDate);

        setLayouts(layoutsWithCounts);
      } else {
        lmLog('No layouts found or API error');
        setLayouts([]);
        setError('No layouts found for this object');
      }
    } catch (error) {
      console.error('Error fetching layouts:', error);
      setError('Failed to load layouts: ' + (error.message || 'Unknown error'));
      setLayouts([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchFields = async () => {
    try {
      // Use the correct endpoint for getting available fields
      const api = new DataApi(`objectconfig/${objectName}`);
      const response = await api.fetchAll();

      lmLog('🔍 LayoutManager fetchFields response:', response);

      if (response?.data?.success) {
        const objectConfig = response.data.data;
        lmLog('🔍 LayoutManager objectConfig:', objectConfig);

        // Extract fields and config.hidden_fields (from merged standard_object config)
        if (objectConfig && objectConfig.fields && Array.isArray(objectConfig.fields)) {
          lmLog('🔍 LayoutManager all fields:', objectConfig.fields);
          lmLog('🔍 LayoutManager calculated fields:', objectConfig.fields.filter(f => f.data_type === 'calculated'));
          setFields(objectConfig.fields);
          const cfg = objectConfig.config;
          const parsed = typeof cfg === 'string' ? (() => { try { return JSON.parse(cfg); } catch { return {}; } })() : (cfg || {});
          setHiddenFields(Array.isArray(parsed.hidden_fields) ? parsed.hidden_fields : []);
        } else {
          lmLog('No fields found in object config or fields is not an array');
          setFields([]);
          setHiddenFields([]);
        }
      } else {
        lmLog('API response not successful');
        setFields([]);
        setHiddenFields([]);
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
      setFields([]);
      setHiddenFields([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const api = new DataApi('roles');
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const rolesData = response.data.data || [];
        setRoles(rolesData);
      } else {
        lmLog('No roles found or API error');
        setRoles([]);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles([]);
    }
  };

  const handleCreateLayout = () => {
    if (!allowLayoutCreate) {
      setError('Layout creation is disabled for this tenant.');
      return;
    }
    if (isLayoutLimitReached) {
      setError(`Layout limit reached (${maxLayouts}).`);
      return;
    }
    setEditingLayout(null);
    setSimpleCreateForm({
      layout_name: '',
      layout_type: 'DETAIL'
    });
    setShowSimpleCreateModal(true);
  };

  const handleEditLayout = async (layout) => {
    try {
      setLoading(true);
      setError('');

      // Fetch sections and fields for this specific layout
      const api = new DataApi(`objectconfig/layouts/${layout.layout_id}/sections`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const layoutData = response.data.data;

        // Handle both old format (array of sections) and new format (object with sections property)
        const sectionsData = Array.isArray(layoutData) ? layoutData : (layoutData.sections || []);
        const layoutMeta = Array.isArray(layoutData) ? {} : layoutData;

        // Transform the data to match the expected format
        const transformedSections = sectionsData.map(section => {
          return {
            id: section.section_id,
            section_name: section.section_name,
            section_label: section.section_label !== undefined && section.section_label !== null ? section.section_label : section.section_name, // Fallback for backward compatibility
            section_order: section.section_order,
            is_collapsible: section.is_collapsible,
            is_collapsed: section.is_collapsed,
            is_visible: section.is_visible,
            is_standard_section: section.is_standard_section,
            layout_columns: section.layout_columns || 2, // Map from database
            fields: (section.fields || []).map(field => ({
              ...field,
              // Map the correct properties from backend
              is_required: field.is_required_layout || field.is_required_base || false,
              is_readonly: field.is_readonly_layout || field.is_readonly_base || false,
              column_span: field.column_span || 1
            }))
          };
        });

        setEditingLayout(layout);
        setLayoutForm({
          layout_name: layoutMeta.layout_name || layout.layout_name,
          layout_type: layoutMeta.layout_type || layout.layout_type || 'DETAIL',
          total_columns: layoutMeta.total_columns || layout.total_columns || 3, // Default to 3 columns if not set
          detail_column_position: layoutMeta.detail_column_position || layout.detail_column_position || 1, // Default to column 1 if not set
          sections: transformedSections
        });

        // Pass `layout` explicitly: `editingLayout` state is not updated until after this tick, so
        // profile-layout field hiding (username, role_id, …) would be skipped on first open after refresh.
        updateAvailableFields(transformedSections, layout);

        setShowLayoutModal(true);
      } else {
        setError('Failed to load layout sections');
      }
    } catch (error) {
      console.error('Error fetching layout sections:', error);
      setError('Failed to load layout sections: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Recompute fields in the "Available Fields" pool.
   * @param {Array} sections - Current layout sections (fields already placed).
   * @param {object|null|undefined} layoutForProfileCheck - Layout row used to detect profile layouts (config.type = profile).
   *   Pass explicitly when opening the modal right after setEditingLayout(layout), because state is stale until the next render.
   *   Omit (undefined) to use `editingLayout` from state (e.g. drag/drop updates while modal is open).
   */
  const updateAvailableFields = (sections, layoutForProfileCheck) => {
    // Get all field IDs that are already added to sections
    const addedFieldIds = new Set();
    sections.forEach(section => {
      section.fields.forEach(field => {
        addedFieldIds.add(field.field_id);
      });
    });

    // Profile layout (config.type = profile, e.g. Edit Profile): hide fields end users should not place on profile form
    const isProfileLayout = (() => {
      const layout = layoutForProfileCheck !== undefined ? layoutForProfileCheck : editingLayout;
      if (!layout) return false;
      const config = layout.config;
      const cfg = typeof config === 'string' ? (() => { try { return JSON.parse(config); } catch { return null; } })() : config;
      return cfg && (cfg.type === 'profile' || cfg.type === 'Profile');
    })();

    const isUserObject = (objectName || '').toLowerCase() === 'user';
    const profileHiddenFieldNames = new Set(['isactive', 'username', 'role_id']);

    const hiddenSet = new Set((hiddenFields || []).map(f => (f || '').toLowerCase()).filter(Boolean));
    // Only list fields not yet placed in any section; once added anywhere, hide from Available Fields.
    const filteredFields = fields.filter(field => {
      const fn = (field.field_name || '').toLowerCase();
      const hideOnUserProfile = isUserObject && isProfileLayout && profileHiddenFieldNames.has(fn);
      return (
        !addedFieldIds.has(field.id) &&
        field.field_name !== 'id' &&
        field.field_name !== 'ID' &&
        !hideOnUserProfile &&
        !hiddenSet.has(fn)
      );
    });

    setAvailableFields(filteredFields);
  };

  // Function to check if a section has required base fields
  const hasRequiredBaseFields = (section) => {
    // Allow deleting system_info section even if it has required base fields
    const isSystemInfoSection = section.section_name && section.section_name.toLowerCase() === 'system_info';
    if (isSystemInfoSection) {
      return false; // Allow deletion of system_info section
    }
    return section.fields && section.fields.some(field => field.is_required_base === true);
  };

  // Function to get the next available section number and name (excluding system_info)
  const getNextSectionNumber = (sections) => {
    // Filter out system_info sections and get existing section numbers
    const existingSections = sections.filter(section =>
      section.section_name && section.section_name.toLowerCase() !== 'system_info'
    );

    // Extract section numbers from existing section names
    const sectionNumbers = existingSections
      .map(section => {
        const match = section.section_name.match(/^section_(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => num > 0)
      .sort((a, b) => a - b);

    // Find the next available number
    let nextNumber = 1;
    for (let i = 0; i < sectionNumbers.length; i++) {
      if (sectionNumbers[i] !== nextNumber) {
        break;
      }
      nextNumber++;
    }

    // If all numbers are sequential, use the next number
    if (sectionNumbers.length > 0 && sectionNumbers[sectionNumbers.length - 1] >= nextNumber) {
      nextNumber = sectionNumbers[sectionNumbers.length - 1] + 1;
    }

    return nextNumber;
  };

  // Function to validate and adjust field column spans when grid layout changes
  const validateFieldColumnSpans = (sections) => {
    return sections.map(section => ({
      ...section,
      fields: section.fields.map(field => ({
        ...field,
        column_span: Math.min(field.column_span || 1, section.layout_columns || 2)
      }))
    }));
  };

  const handleSaveLayout = async (keepModalOpen = false) => {
    try {
      const shouldKeepModalOpen = typeof keepModalOpen === 'boolean' ? keepModalOpen : false;
      setLoading(true);
      setError('');
      setSuccess('');

      if (!editingLayout && !allowLayoutCreate) {
        setError('Layout creation is disabled for this tenant.');
        setLoading(false);
        return;
      }

      if (!layoutForm.layout_name) {
        setError('Layout name is required');
        setLoading(false);
        return;
      }

      const blankSections = layoutForm.sections.filter(section => {
        const isSystemInfoSection = section.section_name
          && section.section_name.toLowerCase() === 'system_info';
        if (isSystemInfoSection) {
          return false;
        }
        return !Array.isArray(section.fields) || section.fields.length === 0;
      });

      if (blankSections.length > 0) {
        const sectionNames = blankSections.map(section =>
          section.section_label || section.section_name || 'Unnamed section'
        );
        setError(`Please add at least one field to ${blankSections.length > 1 ? 'sections' : 'section'}: ${sectionNames.join(', ')}`);
        setLoading(false);
        return;
      }

      lmLog('🔍 === LAYOUT SAVE DEBUG ===');
      lmLog('🔍 Original layoutForm.sections:', layoutForm.sections);

      // Transform the sections data to match the expected API format
      const transformedSections = layoutForm.sections.map(section => {
        lmLog(`🔍 Processing section: ${section.section_name}`);

        const transformedFields = section.fields.map(field => {
          lmLog(`🔍 Field: ${field.field_name}`, {
            is_required: field.is_required,
            is_readonly: field.is_readonly,
            column_span: field.column_span,
            allProperties: Object.keys(field)
          });

          return {
            field_id: field.field_id,
            field_name: field.field_name,
            field_label: field.field_label,
            data_type: field.data_type,
            is_required: field.is_required || false,
            is_readonly: field.is_readonly || false,
            is_reference: field.is_reference || false,
            reference_object: field.reference_object || null,
            picklist_values: field.picklist_values || null,
            lookup_search_fields: field.lookup_search_fields || null,
            display_fields: field.display_fields || null,
            display_order: field.display_order || 0,
            column_span: field.column_span || 1,
            area: field.area || 'detail'
          };
        });

        lmLog(`🔍 Transformed fields for section ${section.section_name}:`, transformedFields);

        return {
          section_id: section.id, // Add section_id for backend to identify existing sections
          section_name: section.section_name,
          section_label: section.section_label !== undefined && section.section_label !== null ? section.section_label : section.section_name, // Fallback for backward compatibility
          section_order: section.section_order || 0,
          is_collapsible: section.is_collapsible || false,
          is_collapsed: section.is_collapsed || false,
          is_visible: section.is_visible !== false, // Default to true if not specified
          layout_columns: section.layout_columns || 2,
          fields: transformedFields
        };
      });

      lmLog('🔍 Final transformedSections:', transformedSections);
      lmLog('🔍 Section orders being sent:', transformedSections.map(s => ({ name: s.section_name, order: s.section_order })));
      lmLog('🔍 === END LAYOUT SAVE DEBUG ===');

      const layoutData = {
        layout_name: layoutForm.layout_name,
        layout_label: layoutForm.layout_label,
        layout_type: editingLayout ? (editingLayout.layout_type || layoutForm.layout_type) : layoutForm.layout_type,
        total_columns: layoutForm.total_columns || 3,
        detail_column_position: layoutForm.detail_column_position || 1,
        sections: transformedSections
      };

      const api = new DataApi('objectconfig/layouts');

      let response;

      if (editingLayout) {
        if (!shouldKeepModalOpen) {
          setShowLayoutModal(false);
        }
        lmLog('🔍 Updating existing layout with ID:', editingLayout.layout_id);
        lmLog('🔍 Data being sent to update API:', layoutData);
        lmLog('🔍 JSON for Postman testing:');
        lmLog(JSON.stringify(layoutData, null, 2));
        //return;
        response = await api.update(layoutData, editingLayout.layout_id);
      } else {
        lmLog('🔍 Creating new layout');
        lmLog('🔍 Data being sent to create API:', { ...layoutData, objectName: objectName });
        response = await api.create({
          ...layoutData,
          objectName: objectName
        });
      }

      lmLog('🔍 API Response:', response);
      lmLog('🔍 Response data structure:', response?.data);

      if (response?.data?.success) {
        setSuccess(`Layout ${editingLayout ? 'updated' : (isCloning ? 'cloned' : 'created')} successfully!`);
        if (!shouldKeepModalOpen) {
          setShowLayoutModal(false);
          setEditingLayout(null);
        }
        // Reset cloning state
        setIsCloning(false);
        setOriginalLayoutName('');
        fetchLayouts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save layout');
      }
    } catch (error) {
      console.error('Error saving layout:', error);
      setError('Failed to save layout: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLayout = async (layoutId) => {
    lmLog('handleDeleteLayout called with layoutId:', layoutId);
    // Find the layout to check if it's standard
    const layout = layouts.find(l => l.layout_id === layoutId);
    lmLog('Found layout:', layout);

    if (isStandardLayout(layout) && !enableStandardObject) {
      lmLog('Layout is standard, preventing deletion');
      lmLog('Layout is_standard value:', layout.is_standard, layout.Is_Standard, layout.IS_STANDARD);
      setError('Standard layouts cannot be deleted. These are system-created layouts.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this layout?')) {
      return;
    }

    try {
      setLoading(true);

      const api = new DataApi('objectconfig/layouts');
      const response = await api.delete(layoutId);

      if (response?.data?.success) {
        setLayouts(layouts.filter(l => l.layout_id !== layoutId));
        setSuccess('Layout deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete layout');
      }
    } catch (error) {
      console.error('Error deleting layout:', error);
      setError('Failed to delete layout: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloneLayout = (layout) => {
    // Generate suggested new layout name with uniqueness check
    let suggestedName = `${layout.layout_name} - Copy`;
    let copyNumber = 2;

    // Check if the suggested name already exists
    while (layouts.some(l => l.layout_name.toLowerCase() === suggestedName.toLowerCase())) {
      suggestedName = `${layout.layout_name} - Copy ${copyNumber}`;
      copyNumber++;
    }

    // Store the layout to clone
    setLayoutToClone(layout);

    // Pre-fill the clone config form
    setCloneConfigForm({
      layout_name: suggestedName,
      layout_type: 'DETAIL'
    });

    // Show the clone configuration modal
    setShowCloneConfigModal(true);
  };

  const handleCloneConfigSubmit = async () => {
    if (!cloneConfigForm.layout_name.trim()) {
      setError('Layout name is required');
      return;
    }

    // Check if the name already exists
    if (layouts.some(l => l.layout_name.toLowerCase() === cloneConfigForm.layout_name.trim().toLowerCase())) {
      setError('A layout with this name already exists. Please choose a different name.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      lmLog('🔄 Cloning layout:', layoutToClone.layout_name);
      lmLog('🔄 New layout name:', cloneConfigForm.layout_name);
      lmLog('🔄 New layout type:', cloneConfigForm.layout_type);

      // Fetch complete layout structure
      const api = new DataApi(`objectconfig/layouts/${layoutToClone.layout_id}/sections`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        // Handle both array of sections and object with sections property
        const rawData = response.data.data;
        const sectionsData = Array.isArray(rawData) ? rawData : (rawData?.sections || []);

        if (!Array.isArray(sectionsData) || sectionsData.length === 0) {
          setError('Failed to load layout for cloning: no sections data returned');
          return;
        }

        lmLog('🔄 Fetched sections for cloning:', sectionsData);

        // System fields that get auto-added by backend
        const systemFieldNames = ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid', 'ownerid'];

        // Transform sections - REMOVE IDs so they're created as new
        const transformedSections = sectionsData.map(section => {
          const sectionNameLower = section.section_name ? section.section_name.toLowerCase() : '';
          const isSystemInfoSection = sectionNameLower === 'system_info';
          const isDetailSection = sectionNameLower === 'detail' || sectionNameLower === 'details' || sectionNameLower === 'main';

          // Filter fields - remove auto-added system fields to prevent duplicates
          const fieldsToInclude = (section.fields || []).filter(field => {
            const fieldNameLower = field.field_name.toLowerCase();

            // If this is the system_info section, exclude auto-added system fields
            if (isSystemInfoSection && systemFieldNames.includes(fieldNameLower)) {
              lmLog(`🔄 Skipping system field "${field.field_name}" from ${section.section_name} section (will be auto-added by backend)`);
              return false;
            }

            // If this is the detail section, exclude ownerid as it gets auto-added
            if (isDetailSection && fieldNameLower === 'ownerid') {
              lmLog(`🔄 Skipping ownerid field from ${section.section_name} section (will be auto-added by backend)`);
              return false;
            }

            return true;
          });

          return {
            // NO section_id - this ensures new sections are created
            section_name: section.section_name,
            section_label: section.section_label !== undefined && section.section_label !== null
              ? section.section_label
              : section.section_name,
            section_order: section.section_order,
            is_collapsible: section.is_collapsible,
            is_collapsed: section.is_collapsed,
            is_visible: section.is_visible,
            is_standard_section: false, // Cloned sections are not standard
            layout_columns: section.layout_columns || 2,
            fields: fieldsToInclude.map(field => ({
              field_id: field.field_id, // Keep field_id to reference the field definition
              field_name: field.field_name,
              field_label: field.field_label,
              data_type: field.data_type,
              is_required: field.is_required_layout || field.is_required_base || false,
              is_readonly: field.is_readonly_layout || field.is_readonly_base || false,
              is_reference: field.is_reference || false,
              reference_object: field.reference_object || null,
              picklist_values: field.picklist_values || null,
              lookup_search_fields: field.lookup_search_fields || null,
              display_fields: field.display_fields || null,
              display_order: field.display_order || 0,
              column_span: field.column_span || 1,
              area: field.area || 'detail'
            }))
          };
        });

        // Close the config modal
        setShowCloneConfigModal(false);

        // Set editing layout to NULL (we're creating, not editing)
        setEditingLayout(null);

        // Set cloning mode
        setIsCloning(true);
        setOriginalLayoutName(layoutToClone.layout_name);

        // Populate the form with cloned data and user-specified name/type
        setLayoutForm({
          layout_name: cloneConfigForm.layout_name,
          layout_type: cloneConfigForm.layout_type,
          total_columns: layoutToClone.total_columns || 3,
          detail_column_position: layoutToClone.detail_column_position || 1,
          sections: transformedSections
        });

        // Update available fields based on what's in the cloned sections
        updateAvailableFields(transformedSections);

        // Open the layout modal for editing
        setShowLayoutModal(true);

        setSuccess(`Layout loaded for cloning. Modify as needed and save.`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to load layout for cloning');
      }
    } catch (error) {
      console.error('Error loading layout for cloning:', error);
      setError('Failed to load layout for cloning: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRoles = (layout) => {
    setSelectedLayout(layout);
    setShowRoleModal(true);
  };

  const handleManageWidgets = (layout) => {
    lmLog('🔧 handleManageWidgets - layout object:', layout);
    lmLog('🔧 handleManageWidgets - layout.layout_id:', layout.layout_id);
    lmLog('🔧 handleManageWidgets - layout.id:', layout.id);
    setSelectedLayoutForWidgets(layout);
    setShowLayoutWidgetManager(true);
  };

  const handleManageRelatedLists = (layout) => {
    setSelectedLayoutForRelatedLists(layout);
    setShowRelatedListManager(true);
  };

  const handleConfigureActions = (layout) => {
    setSelectedLayoutForActions(layout);
    setShowActionsConfigModal(true);
  };

  const handleSimpleCreateSubmit = async () => {
    if (!allowLayoutCreate) {
      setError('Layout creation is disabled for this tenant.');
      return;
    }
    if (!simpleCreateForm.layout_name.trim()) {
      setError('Layout name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const layoutData = {
        layout_name: simpleCreateForm.layout_name,
        layout_type: simpleCreateForm.layout_type,
        sections: []
      };

      const api = new DataApi('objectconfig/layouts');
      const response = await api.create({
        ...layoutData,
        objectName: objectName
      });

      if (response?.data?.success) {
        setSuccess(`Layout "${simpleCreateForm.layout_name}" created successfully!`);
        setShowSimpleCreateModal(false);
        // Reset the simple create form
        setSimpleCreateForm({
          layout_name: '',
          layout_type: 'DETAIL'
        });
        // Refresh the layouts list
        fetchLayouts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to create layout');
      }
    } catch (error) {
      console.error('Error creating layout:', error);
      setError('Failed to create layout: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };



  const getLayoutTypeIcon = (type) => {
    switch (type) {
      case 'edit': return <FaEdit />;
      case 'detail': return <FaEye />;
      case 'list': return <FaList />;
      default: return <FaLayerGroup />;
    }
  };

  const getLayoutTypeBadge = (type) => {
    const variants = {
      edit: 'primary',
      detail: 'info',
      list: 'success'
    };
    return <Badge bg={variants[type] || 'secondary'}>{type}</Badge>;
  };

  // Helper function to check if layout is standard
  const isStandardLayout = (layout) => {
    lmLog('🔍 isStandardLayout called with layout:', layout);
    lmLog('🔍 Layout properties:', {
      is_standard: layout?.is_standard,
      Is_Standard: layout?.Is_Standard,
      IS_STANDARD: layout?.IS_STANDARD,
      type: typeof layout?.is_standard,
      truthy: !!layout?.is_standard
    });

    const result = isStandardLayoutValue(layout);
    lmLog('🔍 isStandardLayout result:', result);
    return result;
  };



  const renderCloneConfigModal = () => (
    <Modal show={showCloneConfigModal} onHide={() => setShowCloneConfigModal(false)} size="md" centered>
      <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)', color: 'white' }}>
        <Modal.Title className='text-white'>
          <FaCopy className="me-2" />
          Clone Layout Configuration
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '24px' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Alert variant="info" className="mb-3">
          <FaInfoCircle className="me-2" />
          <strong>Cloning:</strong> {layoutToClone?.layout_name}
        </Alert>

        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                New Layout Name *
              </Form.Label>
              <Form.Control
                type="text"
                value={cloneConfigForm.layout_name}
                onChange={(e) => setCloneConfigForm({ ...cloneConfigForm, layout_name: e.target.value })}
                placeholder="Enter layout name"
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease'
                }}
              />
              <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                Choose a unique name for the cloned layout
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                Layout Type
              </Form.Label>
              <Form.Control
                type="text"
                value="Detail"
                disabled
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem',
                  backgroundColor: '#f8f9fa',
                }}
              />
              <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                Cloned layouts are created as detail layouts (same as the list in Object Manager).
              </Form.Text>
            </Form.Group>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '16px 24px' }}>
        <Button
          variant="outline-secondary"
          onClick={() => setShowCloneConfigModal(false)}
          style={{ borderRadius: '8px', padding: '8px 20px' }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCloneConfigSubmit}
          disabled={!cloneConfigForm.layout_name.trim() || loading}
          style={{
            background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(36, 58, 115, 0.3)'
          }}
        >
          {loading ? 'Loading...' : 'Continue to Edit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderSimpleCreateModal = () => (
    <Modal show={showSimpleCreateModal} onHide={() => setShowSimpleCreateModal(false)} size="md" centered>
      <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)', color: 'white' }}>
        <Modal.Title className='text-white'>
          <FaPlus className="me-2" />
          Create New Layout
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '24px' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                Layout Name *
              </Form.Label>
              <Form.Control
                type="text"
                value={simpleCreateForm.layout_name}
                onChange={(e) => setSimpleCreateForm({ ...simpleCreateForm, layout_name: e.target.value })}
                placeholder="Enter layout name"
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease'
                }}
              />
              <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                Choose a descriptive name for your layout
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                Layout Type
              </Form.Label>
              <Form.Control
                type="text"
                value="Detail"
                disabled
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem',
                  backgroundColor: '#f8f9fa',
                }}
              />
              <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                This screen lists detail layouts only. Edit (new/record) layouts are assigned per record view elsewhere.
              </Form.Text>
            </Form.Group>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '16px 24px' }}>
        <Button
          variant="outline-secondary"
          onClick={() => setShowSimpleCreateModal(false)}
          style={{ borderRadius: '8px', padding: '8px 20px' }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSimpleCreateSubmit}
          disabled={!simpleCreateForm.layout_name.trim() || loading}
          style={{
            background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(36, 58, 115, 0.3)'
          }}
        >
          {loading ? 'Creating...' : 'Create Layout'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Function to render Layout Configuration Modal
  const renderLayoutConfigModal = () => (
    <Modal show={showLayoutConfigModal} onHide={() => setShowLayoutConfigModal(false)} size="md" centered>
      <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)', color: 'white' }}>
        <Modal.Title className='text-white'>
          <FaCog className="me-2" />
          Layout Configuration
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '24px' }}>
        <Form>
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
              Layout Name *
            </Form.Label>
            <Form.Control
              type="text"
              value={layoutForm.layout_name}
              onChange={(e) => setLayoutForm({ ...layoutForm, layout_name: e.target.value })}
              placeholder="Enter layout name"
              disabled={!editingLayout && !isCloning}
              style={{
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '0.95rem'
              }}
            />
            {!editingLayout && !isCloning && (
              <Form.Text className="text-muted">Layout name was set in the previous step.</Form.Text>
            )}
            {isCloning && (
              <Form.Text className="text-muted">You can modify the layout name for the cloned layout.</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
              Layout Type
            </Form.Label>
            {!editingLayout && !isCloning ? (
              <Form.Control type="text" value={layoutForm.layout_type} disabled />
            ) : (
              <Form.Select
                value={layoutForm.layout_type}
                onChange={(e) => setLayoutForm({ ...layoutForm, layout_type: e.target.value })}
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem'
                }}
              >
                <option value="DETAIL">Detail layout (view &amp; edit)</option>
                {layoutForm.layout_type === 'EDIT' ? (
                  <option value="EDIT">Edit layout (legacy)</option>
                ) : null}
              </Form.Select>
            )}
            {!editingLayout && !isCloning && (
              <Form.Text className="text-muted">Layout type was set in the previous step.</Form.Text>
            )}
            {isCloning && (
              <Form.Text className="text-muted">You can change the layout type for the cloned layout.</Form.Text>
            )}
          </Form.Group>

          {/* Number of Columns - Only show for DETAIL layouts */}
          {layoutForm.layout_type === 'DETAIL' && (
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                Number of Columns
              </Form.Label>
              <Form.Select
                value={layoutForm.total_columns || 3}
                onChange={(e) => {
                  const newTotalColumns = parseInt(e.target.value);
                  const newDetailPosition = Math.min(
                    layoutForm.detail_column_position || 1,
                    newTotalColumns
                  );
                  setLayoutForm({
                    ...layoutForm,
                    total_columns: newTotalColumns,
                    detail_column_position: newDetailPosition
                  });
                }}
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem'
                }}
              >
                <option value={1}>1 Column</option>
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
              </Form.Select>
              <Form.Text className="text-muted" style={{ fontSize: '0.85rem' }}>
                Choose how many columns the detail page should have. This determines the layout structure.
              </Form.Text>
            </Form.Group>
          )}

          {/* Detail Layout Position - Only show for DETAIL layouts with 2+ columns */}
          {layoutForm.layout_type === 'DETAIL' && (layoutForm.total_columns || 3) > 1 && (
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                Detail Layout Position
              </Form.Label>
              <Form.Select
                value={layoutForm.detail_column_position || 1}
                onChange={(e) => setLayoutForm({
                  ...layoutForm,
                  detail_column_position: parseInt(e.target.value)
                })}
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '0.95rem'
                }}
              >
                {Array.from({ length: layoutForm.total_columns || 3 }, (_, i) => i + 1).map(colNum => (
                  <option key={colNum} value={colNum}>
                    {colNum === 1 ? 'Left (Column 1)' :
                      colNum === 2 ? 'Middle (Column 2)' :
                        'Right (Column 3)'}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted" style={{ fontSize: '0.85rem' }}>
                Choose which column the detail layout content appears in. Widgets will be placed in the remaining columns.
              </Form.Text>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '16px 24px' }}>
        <Button
          variant="outline-secondary"
          onClick={() => setShowLayoutConfigModal(false)}
          style={{ borderRadius: '8px', padding: '8px 20px' }}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={async () => {
            if (!layoutForm.layout_name.trim()) {
              setError('Layout name is required');
              return;
            }
            // Save the layout configuration
            await handleSaveLayout(true); // Keep modal open after saving
            setShowLayoutConfigModal(false);
          }}
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(36, 58, 115, 0.3)'
          }}
        >
          {loading ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderLayoutModal = () => (
    <Modal show={showLayoutModal} onHide={() => {
      setShowLayoutModal(false);
      setEditingLayout(null);
      setIsCloning(false);
      setOriginalLayoutName('');
    }} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingLayout ? 'Edit Layout' : (isCloning ? 'Clone Layout' : 'Configure Layout')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-3' style={{ padding: '16px' }}>
        {isCloning && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center">
              <FaInfoCircle className="me-2" size={20} />
              <div>
                <strong>Cloning Layout:</strong> You are creating a copy of "<strong>{originalLayoutName}</strong>".
                Modify the layout name, sections, and fields as needed, then save to create the new layout.
              </div>
            </div>
          </Alert>
        )}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} style={{ zIndex: 9999 }}>
            {success}
          </Alert>
        )}

        {/* Top Buttons */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button
            variant="outline-primary"
            onClick={() => setShowLayoutConfigModal(true)}
          >
            <FaCog className="me-2" />
            Edit Layout Configuration
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const nextSectionNumber = getNextSectionNumber(layoutForm.sections);
              const newSection = {
                id: `section_${Date.now()}`,
                section_name: `section_${nextSectionNumber}`,
                section_label: `Section ${nextSectionNumber}`,
                section_order: layoutForm.sections.length,
                layout_columns: 2,
                fields: []
              };

              const systemInfoIndex = layoutForm.sections.findIndex(section =>
                section.section_name && section.section_name.toLowerCase() === 'system_info'
              );

              let updatedSections;
              if (systemInfoIndex !== -1) {
                updatedSections = [...layoutForm.sections];
                updatedSections.splice(systemInfoIndex, 0, newSection);
              } else {
                updatedSections = [...layoutForm.sections, newSection];
              }

              updatedSections.forEach((section, index) => {
                section.section_order = index;
              });

              setLayoutForm({
                ...layoutForm,
                sections: updatedSections
              });

              setTimeout(() => {
                setSuccess(`Section "${newSection.section_label}" has been added successfully!`);
                setTimeout(() => setSuccess(''), 3000);
              }, 100);
            }}
          >
            <FaPlus className="me-2" />
            Add Section
          </Button>
        </div>

        <DragDropContext
          onDragStart={(result) => {
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
            document.body.style.overflow = 'auto';

          }}
          onDragUpdate={() => {}}
          onDragEnd={(result) => {
            setIsSectionDragging(false);
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';

            const { source, destination, draggableId } = result;

            const isLockedSectionIndex = (sectionIndex) => {
              const sec = layoutForm.sections[sectionIndex];
              return sec && isDetailSectionLockedForSection(sec);
            };

            const isLayoutFieldDrag =
              /^field-/.test(draggableId) || /^sec-\d+-fld-/.test(draggableId);

            if (!destination) {
              logLayoutDropFailure('No valid drop target (cancelled or dropped outside)', {
                draggableId,
                source: source.droppableId,
                index: source.index,
              });
              return;
            }

            if (
              destination.index === source.index &&
              destination.droppableId === source.droppableId
            ) {
              return;
            }

            let handled = false;

            if (source.droppableId === 'available-fields' && destination.droppableId.startsWith('section-')) {
              const sectionIndex = parseInt(destination.droppableId.replace('section-', ''), 10);
              if (Number.isNaN(sectionIndex) || sectionIndex < 0) {
                logLayoutDropFailure('Invalid section drop target', { destination: destination.droppableId });
                return;
              }
              if (isLockedSectionIndex(sectionIndex)) {
                logLayoutDropFailure('Cannot add fields to a locked section', {
                  sectionIndex,
                  sectionName: layoutForm.sections[sectionIndex]?.section_name,
                });
                return;
              }
              const fieldId = draggableId.replace(/^field-/, '');
              const fieldToAdd = availableFields.find(f => String(f.id) === String(fieldId));
              if (!fieldToAdd) {
                logLayoutDropFailure('Field not in Available Fields (already on layout or invalid id)', {
                  fieldId,
                  draggableId,
                });
                return;
              }
              const updatedSections = [...layoutForm.sections];
              const newField = {
                field_id: fieldToAdd.id,
                field_name: fieldToAdd.field_name,
                field_label: fieldToAdd.field_label,
                data_type: fieldToAdd.data_type,
                is_required: false,
                is_readonly: false,
                display_order: destination.index,
                column_span: 1,
                area: 'detail',
                grid_position: destination.index
              };
              updatedSections[sectionIndex].fields.splice(destination.index, 0, newField);
              setLayoutForm({ ...layoutForm, sections: updatedSections });
              updateAvailableFields(updatedSections);
              handled = true;
            }

            if (!handled && source.droppableId.startsWith('section-') && destination.droppableId.startsWith('section-')) {
              const sourceSectionIndex = parseInt(source.droppableId.replace('section-', ''), 10);
              const destSectionIndex = parseInt(destination.droppableId.replace('section-', ''), 10);
              if (isLockedSectionIndex(sourceSectionIndex) || isLockedSectionIndex(destSectionIndex)) {
                logLayoutDropFailure('Cannot move fields to or from a locked section', {
                  sourceSectionIndex,
                  destSectionIndex,
                });
                return;
              }

              const updatedSections = [...layoutForm.sections];

              if (sourceSectionIndex === destSectionIndex) {
                const section = updatedSections[sourceSectionIndex];
                const newFields = [...section.fields];
                const [movedField] = newFields.splice(source.index, 1);
                newFields.splice(destination.index, 0, movedField);
                newFields.forEach((field, index) => {
                  field.display_order = index;
                });
                updatedSections[sourceSectionIndex] = {
                  ...section,
                  fields: newFields
                };
              } else {
                const [movedField] = updatedSections[sourceSectionIndex].fields.splice(source.index, 1);
                updatedSections[destSectionIndex].fields.splice(destination.index, 0, movedField);
                updatedSections[sourceSectionIndex].fields.forEach((field, index) => {
                  field.display_order = index;
                });
                updatedSections[destSectionIndex].fields.forEach((field, index) => {
                  field.display_order = index;
                });
              }

              setLayoutForm({ ...layoutForm, sections: updatedSections });
              handled = true;
            }

            if (!handled && source.droppableId.startsWith('section-') && destination.droppableId === 'available-fields') {
              const sourceSectionIndex = parseInt(source.droppableId.replace('section-', ''), 10);
              if (isLockedSectionIndex(sourceSectionIndex)) {
                logLayoutDropFailure('Cannot remove fields from a locked section', { sourceSectionIndex });
                return;
              }
              const updatedSections = [...layoutForm.sections];
              const fieldToRemove = updatedSections[sourceSectionIndex].fields[source.index];
              if (fieldToRemove?.is_required_base) {
                logLayoutDropFailure('Required base field cannot be removed via drag', {
                  fieldName: fieldToRemove.field_name,
                });
                return;
              }
              updatedSections[sourceSectionIndex].fields.splice(source.index, 1);
              setLayoutForm({ ...layoutForm, sections: updatedSections });
              updateAvailableFields(updatedSections);
              handled = true;
            }

            if (!handled && isLayoutFieldDrag) {
              logLayoutDropFailure('Drop not applied (unsupported target for this drag)', {
                draggableId,
                source: source.droppableId,
                destination: destination.droppableId,
              });
            }
          }}>
          <Row className="flex-nowrap g-2">
            {/* Left Column - Available Fields */}
            <Col md={5}>
              <Card>
                <Card.Header className="layout-card-header">
                  <h6 className="mb-0">
                    <FaList className="me-2" />
                    Available Fields
                    <Badge bg="light" text="dark" className="ms-2">
                      {availableFields.length}
                    </Badge>
                  </h6>
                </Card.Header>
                <Card.Body
                  style={{
                    height: '450px',
                    overflowY: 'auto',
                    overflowX: 'visible',
                    position: 'relative',
                    padding: '12px'
                  }}
                >
                  {/* <Card.Body style={{ height: '600px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}> */}
                  <div className="mb-2">
                    <small className="text-muted">
                      <FaInfoCircle className="me-1" />
                      Drag fields to sections below
                    </small>
                  </div>
                  <Droppable droppableId="available-fields">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                          flex: 1,
                          minHeight: '450px',
                          opacity: isSectionDragging ? 0.5 : 1,
                          filter: isSectionDragging ? 'grayscale(50%)' : 'none',
                          pointerEvents: isSectionDragging ? 'none' : 'auto',
                          border: isSectionDragging ? '1px dashed #ccc' : 'none',
                          borderRadius: isSectionDragging ? '8px' : '0',
                          padding: isSectionDragging ? '12px' : '0',
                          backgroundColor: isSectionDragging ? '#f8f9fa' : 'transparent'
                        }}
                      >
                        <ListGroup variant="flush">
                          {Array.isArray(availableFields) && availableFields.length > 0 ? (
                            availableFields.map((field, index) => (
                              <Draggable key={field.id} draggableId={`field-${field.id}`} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      zIndex: snapshot.isDragging ? 999999 : provided.draggableProps.style?.zIndex,
                                      cursor: isSectionDragging ? 'not-allowed' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                                      opacity: isSectionDragging ? 0.6 : 1,
                                      pointerEvents: isSectionDragging ? 'none' : 'auto'
                                    }}
                                  >
                                    <ListGroup.Item
                                      className={`field-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                      style={{
                                        background: snapshot.isDragging ? '#e3f2fd' : 'white',
                                        border: snapshot.isDragging ? '2px solid #2196f3' : '1px solid #ddd',
                                        transform: snapshot.isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
                                        // zIndex: snapshot.isDragging ? 9999 : 'auto'
                                        zIndex: snapshot.isDragging ? 999999 : 'auto',
                                        position: snapshot.isDragging ? 'relative' : 'static'
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                          <div className="fw-bold text-primary">{field.field_label}</div>
                                          <small className="text-muted">{field.field_name}</small>
                                        </div>
                                        <div className="d-flex align-items-center">
                                          <div className="drag-handle me-2" style={{ cursor: 'grab' }}>
                                            <FaGripVertical size={12} />
                                          </div>
                                          <Badge bg="secondary">{field.data_type}</Badge>
                                        </div>
                                      </div>
                                    </ListGroup.Item>
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <ListGroup.Item className="text-center text-muted py-4">
                              {loading ? (
                                <div>
                                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                  Loading fields...
                                </div>
                              ) : (
                                <div>
                                  <FaCheckCircle className="text-success mb-2" size={24} />
                                  <div>All fields have been added</div>
                                </div>
                              )}
                            </ListGroup.Item>
                          )}
                          {provided.placeholder}
                        </ListGroup>
                      </div>
                    )}
                  </Droppable>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column - Layout Sections */}
            <Col md={7} className="p-0">
              <Card>
                <Card.Header className="layout-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <FaLayerGroup className="me-2" />
                      Layout Sections
                      <Badge bg="light" text="dark" className="ms-2">
                        {layoutForm.sections.length}
                      </Badge>
                    </h6>

                  </div>
                </Card.Header>
                <Card.Body style={{ height: '450px', overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '12px' }}>
                  {layoutForm.sections.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <FaLayerGroup size={64} className="mb-3 text-muted" />
                      <h6>No Sections Added</h6>
                      <p className="mb-3">Create sections to organize your fields</p>
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          const nextSectionNumber = getNextSectionNumber(layoutForm.sections);
                          const newSection = {
                            id: `section_${Date.now()}`,
                            section_name: `section_${nextSectionNumber}`, // Backend identifier format
                            section_label: `Section ${nextSectionNumber}`, // Default user-friendly display name
                            layout_columns: 2, // Default to 2 columns
                            fields: []
                          };

                          // Always add before system_info section if it exists
                          const systemInfoIndex = layoutForm.sections.findIndex(section =>
                            section.section_name && section.section_name.toLowerCase() === 'system_info'
                          );

                          let updatedSections;
                          if (systemInfoIndex !== -1) {
                            // Insert just above the "System Information" section
                            updatedSections = [...layoutForm.sections];
                            updatedSections.splice(systemInfoIndex, 0, newSection);
                          } else {
                            // No "System Information" section found, add at the end
                            updatedSections = [...layoutForm.sections, newSection];
                          }

                          // Update section orders for all sections
                          updatedSections.forEach((section, index) => {
                            section.section_order = index;
                            lmLog(`Updated section "${section.section_label}" order to: ${index}`);
                          });

                          setLayoutForm({
                            ...layoutForm,
                            sections: updatedSections
                          });

                          // Debug: Check if section_order was set correctly
                          lmLog('Final sections with orders (Add First):', updatedSections.map(s => ({
                            name: s.section_name,
                            label: s.section_label,
                            order: s.section_order
                          })));
                        }}
                      >
                        <FaPlus className="me-1" />
                        Add First Section
                      </Button>
                    </div>
                  ) : (
                    /* Plain wrapper — not a Droppable. A parent Droppable here had no Draggable children
                       (only nested section-N droppables), which caused dnd runChecks warnings and drops
                       from Available Fields to register as destination "sections" instead of section-N. */
                    <div
                      style={{
                        flex: 1,
                        border: '3px dashed #e9ecef',
                        borderRadius: '12px',
                        padding: '20px',
                        backgroundColor: 'rgba(248, 249, 250, 0.5)',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                      }}
                    >
                          {layoutForm.sections.map((section, index) => (

                            <React.Fragment key={`section-${index}`}>
                              {/* Drop zone indicator before first section */}
                              {index === 0 && (
                                <div
                                  style={{
                                    height: '30px',
                                    margin: '8px 0',
                                    border: '2px dashed #dee2e6',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(248, 249, 250, 0.8)',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <small className="text-muted">
                                    Drop zone
                                  </small>
                                </div>
                              )}
                              <div
                                className="section-item"
                                style={{
                                  cursor: 'default',
                                  background: 'white',
                                  border: '1px solid #ddd',
                                  borderRadius: '8px',
                                  padding: '8px',
                                  margin: '8px 0'
                                }}
                              >
                                <div
                                  className="mb-3 section-card"
                                  style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <div className="section-card-header" style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="d-flex align-items-center">

                                        <Form.Control
                                          type="text"
                                          value={section.section_label !== undefined && section.section_label !== null ? section.section_label : ''}
                                          onChange={(e) => {
                                            const updatedSections = [...layoutForm.sections];
                                            updatedSections[index].section_label = e.target.value;
                                            setLayoutForm({ ...layoutForm, sections: updatedSections });
                                          }}
                                          placeholder="Section name"
                                          className="section-name-input"
                                          style={{
                                            backgroundColor: 'white',
                                            cursor: 'text'
                                          }}
                                        />
                                      </div>
                                      <div className="btn-group btn-group-sm">
                                        {isDetailSectionLockedForSection(section) && (
                                          <small className="text-muted align-self-center me-2" title="Detail section is locked (user standard layout)">Locked</small>
                                        )}
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          disabled={index === 0 || isDetailSectionLockedForSection(section)}
                                          onClick={() => {
                                            if (index === 0) return;
                                            const updatedSections = Array.from(layoutForm.sections);
                                            const [moved] = updatedSections.splice(index, 1);
                                            updatedSections.splice(0, 0, moved);
                                            updatedSections.forEach((s, i) => { s.section_order = i; });
                                            lmLog('Move to first - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                            setLayoutForm({ ...layoutForm, sections: updatedSections });
                                          }}
                                          title="Move section to first"
                                        >
                                          <FaAngleDoubleUp />
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          disabled={index === 0 || isDetailSectionLockedForSection(section)}
                                          onClick={() => {
                                            if (index === 0) return;
                                            const updatedSections = Array.from(layoutForm.sections);
                                            const [moved] = updatedSections.splice(index, 1);
                                            updatedSections.splice(index - 1, 0, moved);
                                            updatedSections.forEach((s, i) => { s.section_order = i; });
                                            lmLog('Move up - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                            setLayoutForm({ ...layoutForm, sections: updatedSections });
                                          }}
                                          title="Move section up"
                                          className="ms-1"
                                        >
                                          <FaArrowUp />
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          disabled={index === layoutForm.sections.length - 1 || isDetailSectionLockedForSection(section)}
                                          onClick={() => {
                                            if (index === layoutForm.sections.length - 1) return;

                                            // Check if the next section is "System Information"
                                            const nextSection = layoutForm.sections[index + 1];
                                            if (nextSection && nextSection.section_name && nextSection.section_name.toLowerCase() === 'system_info') {
                                              // Allow moving to just before "System Information" section
                                              const updatedSections = Array.from(layoutForm.sections);
                                              const [moved] = updatedSections.splice(index, 1);
                                              updatedSections.splice(index + 1, 0, moved);
                                              updatedSections.forEach((s, i) => { s.section_order = i; });
                                              lmLog('Move down (before system_info) - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                              setLayoutForm({ ...layoutForm, sections: updatedSections });
                                              return;
                                            }



                                            const updatedSections = Array.from(layoutForm.sections);
                                            const [moved] = updatedSections.splice(index, 1);
                                            updatedSections.splice(index + 1, 0, moved);
                                            updatedSections.forEach((s, i) => { s.section_order = i; });
                                            lmLog('Move down - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                            setLayoutForm({ ...layoutForm, sections: updatedSections });
                                          }}
                                          title="Move section down"
                                          className="ms-1"

                                        >
                                          <FaArrowDown />
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          disabled={index === layoutForm.sections.length - 1 || isDetailSectionLockedForSection(section)}
                                          onClick={() => {
                                            if (index === layoutForm.sections.length - 1) return;

                                            // Move to the end (allow moving anywhere including below system_info)
                                            const updatedSections = Array.from(layoutForm.sections);
                                            const [moved] = updatedSections.splice(index, 1);
                                            updatedSections.push(moved);
                                            updatedSections.forEach((s, i) => { s.section_order = i; });
                                            lmLog('Move to last - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                            setLayoutForm({ ...layoutForm, sections: updatedSections });
                                          }}
                                          title="Move section to last"
                                          className="ms-1"
                                        >
                                          <FaAngleDoubleDown />
                                        </Button>
                                        {(() => {
                                          const isSystemInfoSection = section.section_name && section.section_name.toLowerCase() === 'system_info';
                                          const isLockedDetailSection = isDetailSectionLockedForSection(section);
                                          // Standard sections: deletable when Enable Standard is on, or system_info special case; never delete locked detail section
                                          return (
                                            !isLockedDetailSection &&
                                            (enableStandardObject || !section.is_standard_section || isSystemInfoSection)
                                          );
                                        })() && (
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              disabled={hasRequiredBaseFields(section)}
                                              title={hasRequiredBaseFields(section) ?
                                                `Cannot delete section with required base fields. Move the required fields to another section first.` :
                                                'Delete Section'
                                              }
                                              onClick={() => {
                                                const isSystemInfoSection = section.section_name && section.section_name.toLowerCase() === 'system_info';

                                                // Check if section has required base fields (but allow system_info to be deleted)
                                                if (!isSystemInfoSection && hasRequiredBaseFields(section)) {
                                                  const requiredFields = section.fields.filter(field => field.is_required_base);
                                                  const fieldNames = requiredFields.map(field => field.field_label || field.field_name).join(', ');
                                                  alert(`Cannot delete section "${section.section_label || section.section_name}" because it contains required base fields: ${fieldNames}. Please move these fields to another section first.`);
                                                  return;
                                                }

                                                // Confirm deletion for system_info section
                                                if (isSystemInfoSection) {
                                                  if (!window.confirm(`Are you sure you want to delete the "${section.section_label || section.section_name}" section? This will remove all system information fields from this layout.`)) {
                                                    return;
                                                  }
                                                }

                                                const updatedSections = layoutForm.sections.filter((_, i) => i !== index);
                                                // Update section orders after deletion
                                                updatedSections.forEach((s, i) => { s.section_order = i; });
                                                lmLog('Delete section - Updated orders:', updatedSections.map(s => ({ name: s.section_name, order: s.section_order })));
                                                setLayoutForm({ ...layoutForm, sections: updatedSections });
                                              }}
                                            >
                                              <FaTrash />
                                            </Button>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ padding: '16px' }}>
                                    <div className="section-fields">
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6>Fields in this section:</h6>
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted me-2">Grid Layout:</small>
                                          <Form.Select
                                            size="sm"
                                            style={{ width: 'auto' }}
                                            value={section.layout_columns || 2}
                                            onChange={(e) => {
                                              const newColumnCount = parseInt(e.target.value);
                                              const updatedSections = [...layoutForm.sections];
                                              updatedSections[index].layout_columns = newColumnCount;

                                              // Validate and adjust field column spans
                                              updatedSections[index].fields = updatedSections[index].fields.map(field => ({
                                                ...field,
                                                column_span: Math.min(field.column_span || 1, newColumnCount)
                                              }));

                                              setLayoutForm({ ...layoutForm, sections: updatedSections });
                                            }}
                                          >
                                            <option value={1}>1 Column</option>
                                            <option value={2}>2 Columns</option>
                                            <option value={3}>3 Columns</option>
                                            <option value={4}>4 Columns</option>
                                          </Form.Select>
                                        </div>
                                      </div>

                                      <Droppable droppableId={`section-${index}`} isDropDisabled={isDetailSectionLockedForSection(section)}>
                                        {(provided, snapshot) => (
                                          <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={`section-drop-zone ${snapshot.isOver && !isSectionDragging && !isDetailSectionLockedForSection(section) ? 'drop-over' : ''}`}
                                            style={{
                                              minHeight: '100px',
                                              border: isSectionDragging ? '1px dashed #ccc' : (snapshot.isOver ? '2px dashed #007bff' : '1px dashed #dee2e6'),
                                              borderRadius: '8px',
                                              padding: '16px',
                                              backgroundColor: isSectionDragging ? '#f8f9fa' : (snapshot.isOver ? '#e3f2fd' : 'transparent'),
                                              opacity: isSectionDragging ? 0.5 : 1,
                                              filter: isSectionDragging ? 'grayscale(50%)' : 'none',
                                              pointerEvents: isSectionDragging ? 'none' : 'auto'
                                            }}
                                            onMouseDown={(e) => {
                                              // Prevent section dragging when interacting with field drop zone
                                              e.stopPropagation();
                                            }}
                                          >
                                            {section.fields.length === 0 ? (
                                              <div className="text-center text-muted py-4">
                                                <FaLayerGroup size={32} className="mb-2" />
                                                <p className="mb-0">
                                                  {snapshot.isOver ? 'Drop field here' : 'No fields added to this section'}
                                                </p>
                                              </div>
                                            ) : (
                                              <div className="grid-layout" style={{
                                                display: 'grid',
                                                gridTemplateColumns: `repeat(${section.layout_columns || 2}, 1fr)`,
                                                gap: '12px'
                                              }}>
                                                {section.fields.map((field, fieldIndex) => (
                                                  <Draggable
                                                    key={`${index}-${field.field_id}-${fieldIndex}`}
                                                    draggableId={`sec-${index}-fld-${field.field_id}-${fieldIndex}`}
                                                    index={fieldIndex}
                                                    isDragDisabled={isDetailSectionLockedForSection(section)}
                                                  >
                                                    {(provided, snapshot) => (
                                                      <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`grid-field-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                                        style={{
                                                          ...provided.draggableProps.style,
                                                          cursor: isDetailSectionLockedForSection(section) ? 'default' : (isSectionDragging ? 'not-allowed' : (snapshot.isDragging ? 'grabbing' : 'grab')),
                                                          gridColumn: field.column_span > 1 ? `span ${field.column_span}` : 'span 1',
                                                          opacity: isSectionDragging ? 0.6 : 1,
                                                          pointerEvents: isSectionDragging ? 'none' : 'auto'
                                                        }}
                                                        onMouseDown={(e) => {
                                                          lmLog('🖱️ Field mouse down:', field.field_label);
                                                          lmLog('🖱️ Is section dragging:', isSectionDragging);
                                                          lmLog('🖱️ Snapshot isDragging:', snapshot.isDragging);
                                                          // Prevent section dragging when dragging fields
                                                          e.stopPropagation();
                                                        }}
                                                      >
                                                        <div
                                                          className={`field-card ${snapshot.isDragging ? 'dragging' : ''}`}
                                                          style={{
                                                            background: snapshot.isDragging ? '#e3f2fd' : 'white',
                                                            border: snapshot.isDragging ? '2px solid #2196f3' : '1px solid #ddd',
                                                            transform: snapshot.isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
                                                            // zIndex: snapshot.isDragging ? 9999 : 'auto',
                                                            zIndex: snapshot.isDragging ? 999999 : 'auto',
                                                            position: snapshot.isDragging ? 'relative' : 'static',
                                                            borderRadius: '6px',
                                                            padding: '8px'
                                                          }}

                                                          onMouseEnter={() => {
                                                            if (snapshot.isDragging) {
                                                              lmLog('🎯 Field card being dragged:', field.field_label);
                                                              lmLog('🎯 Snapshot isDragging:', snapshot.isDragging);
                                                            }
                                                          }}
                                                        >
                                                          <div className="field-header">
                                                            <div className="field-info">
                                                              <div className="field-label">{field.field_label}</div>
                                                              <small className="field-name">{field.field_name}</small>
                                                            </div>
                                                            <div className="field-actions">
                                                              <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => {
                                                                  if (isDetailSectionLockedForSection(section)) return;
                                                                  const currentField = { ...field };
                                                                  const maxColumns = layoutForm.sections[index].layout_columns || 2;

                                                                  // Ensure column span doesn't exceed grid layout
                                                                  if (currentField.column_span > maxColumns) {
                                                                    currentField.column_span = maxColumns;
                                                                  }

                                                                  // Auto-set specific system fields as read-only for edit layouts
                                                                  const readonlySystemFields = ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'];
                                                                  if (['EDIT', 'DETAIL'].includes(String(layoutForm.layout_type || '').toUpperCase()) && readonlySystemFields.includes(currentField.field_name.toLowerCase())) {
                                                                    currentField.is_readonly = true;
                                                                  }

                                                                  // Add section_name to the field for reference in modal
                                                                  currentField.section_name = layoutForm.sections[index].section_name;

                                                                  setEditingField(currentField);
                                                                  setEditingFieldSectionIndex(index);
                                                                  setEditingFieldIndex(fieldIndex);
                                                                  setShowFieldConfigModal(true);
                                                                }}
                                                                disabled={isDetailSectionLockedForSection(section)}
                                                                title={isDetailSectionLockedForSection(section) ? 'Detail section is locked' : 'Configure Field'}
                                                              >
                                                                <FaCog size={10} />
                                                              </Button>
                                                              <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                disabled={!!field.is_required_base || isDetailSectionLockedForSection(section)}
                                                                onClick={() => {
                                                                  if (field.is_required_base) return;
                                                                  if (isDetailSectionLockedForSection(section)) return;
                                                                  const updatedSections = [...layoutForm.sections];
                                                                  updatedSections[index].fields.splice(fieldIndex, 1);
                                                                  setLayoutForm({ ...layoutForm, sections: updatedSections });
                                                                  updateAvailableFields(updatedSections);
                                                                }}
                                                                title={field.is_required_base ? 'Required base fields cannot be removed' : (isDetailSectionLockedForSection(section) ? 'Detail section is locked' : 'Remove Field')}
                                                              >
                                                                <FaTrash size={10} />
                                                              </Button>
                                                            </div>
                                                          </div>
                                                          <div className="field-badges">
                                                            <Badge bg="info" size="sm" className="me-1">{field.data_type}</Badge>
                                                            {/* Show base-level badges first, then layout-level only if no base-level exists */}
                                                            {field.is_required_base ? (
                                                              <Badge bg="dark" size="sm" className="me-1">Base Required</Badge>
                                                            ) : field.is_required && (
                                                              <Badge bg="danger" size="sm" className="me-1">Required</Badge>
                                                            )}
                                                            {field.is_readonly_base ? (
                                                              <Badge bg="secondary" size="sm" className="me-1">Base Read Only</Badge>
                                                            ) : field.is_readonly && (
                                                              <Badge bg="warning" size="sm" className="me-1">Read Only</Badge>
                                                            )}
                                                            {field.column_span > 1 && <Badge bg="success" size="sm" className="me-1">Span: {field.column_span}</Badge>}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                              </div>
                                            )}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </div>
                                  </div>
                                </div>
                              </div>


                            </React.Fragment>
                          ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </DragDropContext>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => {
          setShowLayoutModal(false);
          setIsCloning(false);
          setOriginalLayoutName('');
        }}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveLayout}
          disabled={loading || !layoutForm.layout_name}
        >
          {loading ? 'Saving...' : (editingLayout ? 'Update Layout' : (isCloning ? 'Clone Layout' : 'Save Layout'))}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="layout-manager">
      {!objectName ? (
        <div className="text-center py-4">
          <p>No object selected. Please select an object to manage layouts.</p>
        </div>
      ) : (
        <>
          {error && !showLayoutModal && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {initialLoading && (
            <div className="text-center py-4">
              <div className="loading-spinner mb-3"></div>
              <p>Loading layout manager...</p>
            </div>
          )}

          {!initialLoading && (
            <>
              <div className="layout-manager-header">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h4>Layouts for {objectName}</h4>
                    <p className="text-muted">Create and manage layouts with sections and role assignments</p>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Button
                      variant="outline-primary"
                      onClick={() => setAssignmentsModalOpen(true)}
                    >
                      <FaUsers className="me-1" />
                      View assignments
                    </Button>
                    {allowLayoutCreate && (
                      <Button
                        variant="primary"
                        onClick={handleCreateLayout}
                        disabled={!canCreateLayout}
                        title={!canCreateLayout
                          ? `Layout limit reached (${maxLayouts}).`
                          : undefined}
                      >
                        <FaPlus className="me-1" />
                        Create Layout
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {loading && layouts.length === 0 ? (
                <div className="text-center py-4">
                  <div className="loading-spinner mb-3"></div>
                  <p>Loading layouts...</p>
                </div>
              ) : visibleLayouts.length === 0 ? (
                <Card className="empty-state">
                  <Card.Body className="text-center py-5">
                    <FaLayerGroup size={48} className="text-muted mb-3" />
                    <h6>No Layouts Found</h6>
                    <p className="text-muted mb-3">
                      Create your first layout to organize fields and assign roles.
                    </p>
                    <small className="text-muted">
                      Note: Standard layouts are automatically created by the system and cannot be deleted.
                    </small>
                  </Card.Body>
                </Card>
              ) : (
                <Row>
                  {visibleLayouts.map((layout) => {
                    lmLog("🎯 Rendering layout:", {
                      id: layout.layout_id,
                      name: layout.layout_name,
                      is_standard: layout.is_standard,
                      Is_Standard: layout.Is_Standard,
                      IS_STANDARD: layout.IS_STANDARD,
                      allProps: Object.keys(layout)
                    });
                    return (
                      <Col key={layout.layout_id} md={6} lg={4} className="mb-3">
                        <Card className="layout-card h-100">
                          <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <span className="me-2">{getLayoutTypeIcon(layout.layout_type)}</span>
                              <strong>{layout.layout_name}</strong>
                              {isStandardLayout(layout) && (
                                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                  Standard
                                </Badge>
                              )}
                            </div>
                            <Dropdown>
                              <Dropdown.Toggle variant="outline-secondary" size="sm">
                                <FaCog className="text-white" size={14} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleEditLayout(layout)}>
                                  <FaEdit className="me-2" />
                                  Edit Layout
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleCloneLayout(layout)}>
                                  <FaCopy className="me-2" />
                                  Clone Layout
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => handleAssignRoles(layout)}>
                                  <FaUserPlus className="me-2" />
                                  Assign Roles
                                </Dropdown.Item>
                                {layout.layout_type?.toLowerCase() === 'detail' && (
                                  <Dropdown.Item onClick={() => handleConfigureActions(layout)}>
                                    <FaCog className="me-2" />
                                    Configure Actions
                                  </Dropdown.Item>
                                )}

                                <Dropdown.Divider />
                                {(() => {
                                  const isStandard = isStandardLayout(layout);
                                  const deleteBlocked = isStandard && !enableStandardObject;
                                  lmLog('🎯 Dropdown rendering for layout:', layout.layout_name, 'isStandard:', isStandard);
                                  return deleteBlocked ? (
                                    <Dropdown.Item
                                      className="text-muted"
                                      style={{
                                        cursor: 'not-allowed',
                                        opacity: 0.6,
                                        pointerEvents: 'none'
                                      }}
                                      title="Standard layouts cannot be deleted unless Enable Standard is on in your subscription"
                                    >
                                      <FaTrash className="me-2" />
                                      Delete Layout (Disabled)
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item
                                      onClick={() => handleDeleteLayout(layout.layout_id)}
                                      className="text-danger"
                                      style={{ cursor: 'pointer' }}
                                      title={isStandard ? 'Delete standard layout' : 'Delete Layout'}
                                    >
                                      <FaTrash className="me-2" />
                                      Delete Layout
                                    </Dropdown.Item>
                                  );
                                })()}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Card.Header>
                          <Card.Body>
                            <div className="mb-2">
                              {getLayoutTypeBadge(layout.layout_type)}
                            </div>

                            {/* Layout Statistics */}
                            <div className="layout-stats mb-3">
                              <div className="row text-center">
                                <div className="col-4">
                                  <div className="stat-item">
                                    <div className="stat-number">{layout.section_count}</div>
                                    <div className="stat-label">Sections</div>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="stat-item">
                                    <div className="stat-number">{layout.field_count}</div>
                                    <div className="stat-label">Fields</div>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="stat-item">
                                    <div className="stat-number">{layout.role_count}</div>
                                    <div className="stat-label">Roles</div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Layout Status */}
                            <div className="layout-status mb-2">
                              <Badge bg={layout.is_active !== false ? 'success' : 'secondary'} className="me-2">
                                {layout.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                              {isStandardLayout(layout) && (
                                <Badge bg="warning" text="dark" className="me-2">
                                  System Layout
                                </Badge>
                              )}
                              <small className="text-muted">
                                Modified: {layout.lastmodifieddate
                                  ? new Date(layout.lastmodifieddate).toLocaleDateString()
                                  : 'N/A'}
                              </small>
                            </div>
                          </Card.Body>
                          <Card.Footer className="text-center">
                            <div className="btn-group-vertical w-100" role="group">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditLayout(layout)}
                                className="mb-2"
                              >
                                <FaEdit className="me-1" />
                                Edit Layout
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleAssignRoles(layout)}
                                className="mb-2"
                              >
                                <FaUsers className="me-1" />
                                Assign Roles
                              </Button>
                              {layout.layout_type?.toLowerCase() === 'detail' && (
                                <>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleManageRelatedLists(layout)}
                                    className="mb-2"
                                  >
                                    <FaLayerGroup className="me-1" />
                                    Manage Related Lists
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleManageWidgets(layout)}
                                  >
                                    <FaList className="me-1" />
                                    Manage Widgets
                                  </Button>
                                </>
                              )}
                            </div>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </>
          )}
        </>
      )}

      {renderCloneConfigModal()}
      {renderSimpleCreateModal()}
      {renderLayoutModal()}
      {renderLayoutConfigModal()}

      <RoleAssignmentModal
        show={showRoleModal}
        onHide={() => setShowRoleModal(false)}
        layout={selectedLayout}
        roles={roles}
        onRolesAssigned={(updatedLayout) => {
          setLayouts(layouts.map(l => l.layout_id === updatedLayout.layout_id ? updatedLayout : l));
          setShowRoleModal(false);
          setSuccess('Roles assigned successfully!');
          setTimeout(() => setSuccess(''), 3000);
        }}
      />

      {/* Field Configuration Modal */}
      <Modal show={showFieldConfigModal} onHide={() => setShowFieldConfigModal(false)} size="md" centered>
        <Modal.Header closeButton style={{ textcolor: "white", background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)', color: 'white', borderBottom: 'none' }}>
          <style>
            {`
              .modal-header .btn-close {
                filter: invert(1) brightness(100);
color : white;
              }
            `}
          </style>
          <Modal.Title style={{ color: 'white' }}>
            <FaCog className="me-2" size={18} style={{ color: 'white' }} />
            Field Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px', backgroundColor: '#f8f9fa' }}>
          {editingField && (
            <div>
              <div style={{
                background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
                color: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 4px 12px rgba(36, 58, 115, 0.3)'
              }}>
                <div className="d-flex align-items-center mb-2">
                  <h5 className="mb-0" style={{ color: 'white', fontWeight: '600' }}>{editingField.field_label}</h5>
                  <Badge bg="light" text="dark" className="ms-3" style={{ fontSize: '0.8rem' }}>
                    {editingField.data_type}
                  </Badge>
                </div>
                <p className="mb-2" style={{ color: '#e3f2fd', fontSize: '0.9rem' }}>
                  {editingField.field_name}
                </p>

                {/* Field Status Display */}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {editingField.is_required_base && (
                    <Badge bg="dark" size="sm">
                      <i className="fas fa-lock me-1"></i>Base Required
                    </Badge>
                  )}
                  {editingField.is_readonly_base && (
                    <Badge bg="secondary" size="sm">
                      <i className="fas fa-lock me-1"></i>Base Read Only
                    </Badge>
                  )}
                  {editingField.is_required && !editingField.is_required_base && (
                    <Badge bg="danger" size="sm">
                      <i className="fas fa-edit me-1"></i>Layout Required
                    </Badge>
                  )}
                  {editingField.is_readonly && !editingField.is_readonly_base && (
                    <Badge bg="warning" size="sm">
                      <i className="fas fa-edit me-1"></i>Layout Read Only
                    </Badge>
                  )}
                </div>

                <div style={{ color: '#e3f2fd', fontSize: '0.85rem' }}>
                  <FaLayerGroup className="me-1" />
                  Section Grid: {layoutForm.sections[editingFieldSectionIndex]?.layout_columns || 2} columns
                </div>
              </div>

              <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Form>
                  {['EDIT', 'DETAIL'].includes(String(layoutForm.layout_type || '').toUpperCase()) && (
                    <Form.Group className="mb-4">
                      <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                        Field Properties
                      </Form.Label>
                      {String(layoutForm.layout_type || '').toUpperCase() === 'DETAIL' && (
                        <p className="text-muted small mb-3">
                          Required and read-only here apply when users <strong>create or edit</strong> records with this layout (detail layout is also used for edit).
                        </p>
                      )}

                      {/* Base-level settings info */}
                      {(editingField.is_required_base || editingField.is_readonly_base) && (
                        <div className="alert alert-info mb-3" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-info-circle me-2"></i>
                          <strong>Base-level settings detected:</strong>
                          {editingField.is_required_base && (
                            <div className="mt-1">
                              • This field is <strong>required at field level</strong> and cannot be overridden
                            </div>
                          )}
                          {editingField.is_readonly_base && (
                            <div className="mt-1">
                              • This field is <strong>read-only at field level</strong> and cannot be overridden
                            </div>
                          )}
                          <div className="mt-2 text-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            <strong>Note:</strong> When base-level settings exist, both required and read-only options are disabled to maintain consistency.
                          </div>
                        </div>
                      )}

                      <div className="row">
                        <div className="col-6">
                          <Form.Check
                            type="checkbox"
                            id="isRequired"
                            label="Required Field"
                            checked={editingField.is_required || false}
                            onChange={(e) => {
                              setEditingField({
                                ...editingField,
                                is_required: e.target.checked,
                                is_required_layout: e.target.checked, // Save to layout-level property
                                is_readonly: e.target.checked ? false : editingField.is_readonly,
                                is_readonly_layout: e.target.checked ? false : editingField.is_readonly_layout // Update layout-level property
                              });
                            }}
                            disabled={
                              ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(editingField.field_name.toLowerCase()) ||
                              editingField.is_required_base || // Disable if required at base level
                              editingField.is_readonly_base // Disable if readonly at base level
                            }
                            style={{
                              padding: '12px',
                              border: '1px solid #e9ecef',
                              borderRadius: '8px',
                              backgroundColor: editingField.is_required ? '#e3f2fd' : 'white',
                              borderColor: editingField.is_required ? '#2196f3' : '#e9ecef',
                              transition: 'all 0.2s ease',
                              opacity: ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(editingField.field_name.toLowerCase()) || editingField.is_required_base || editingField.is_readonly_base ? 0.6 : 1
                            }}
                          />
                          {editingField.is_required_base && (
                            <small style={{ color: '#2196f3', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                              <i className="fas fa-lock me-1"></i>
                              Required at field level (cannot be changed)
                            </small>
                          )}
                        </div>
                        <div className="col-6">

                          <Form.Check
                            type="checkbox"
                            id="isReadonly"
                            label="Read Only Field"
                            checked={editingField.is_readonly || false}
                            onChange={(e) => {
                              // For system fields, always keep them read-only
                              const readonlySystemFields = ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'];
                              const newReadOnlyValue = readonlySystemFields.includes(editingField.field_name.toLowerCase()) ? true : e.target.checked;
                              setEditingField({
                                ...editingField,
                                is_readonly: newReadOnlyValue,
                                is_readonly_layout: newReadOnlyValue, // Save to layout-level property
                                is_required: newReadOnlyValue ? false : editingField.is_required,
                                is_required_layout: newReadOnlyValue ? false : editingField.is_required_layout // Update layout-level property
                              });
                            }}
                            disabled={
                              ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(editingField.field_name.toLowerCase()) ||
                              editingField.is_readonly_base // Disable if readonly at base level
                            }
                            style={{
                              padding: '12px',
                              border: '1px solid #e9ecef',
                              borderRadius: '8px',
                              backgroundColor: editingField.is_readonly ? '#fff3e0' : 'white',
                              borderColor: editingField.is_readonly ? '#ff9800' : '#e9ecef',
                              transition: 'all 0.2s ease',
                              opacity: ['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(editingField.field_name.toLowerCase()) || editingField.is_required_base || editingField.is_readonly_base ? 0.6 : 1
                            }}
                          />
                          {editingField.is_readonly_base && (
                            <small style={{ color: '#ff9800', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                              <i className="fas fa-lock me-1"></i>
                              Read-only at field level (cannot be changed)
                            </small>
                          )}
                          {['createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(editingField.field_name.toLowerCase()) && (
                            <small style={{ color: '#ff9800', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                              System fields are always read-only
                            </small>
                          )}
                        </div>
                      </div>
                      <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                        A field cannot be both required and read-only at the same time.
                        {(editingField.is_required_base || editingField.is_readonly_base) && (
                          <span className="text-info"> Base-level settings take priority over layout-level settings.</span>
                        )}
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                      Column Span
                    </Form.Label>
                    <Form.Select
                      value={editingField.column_span || 1}
                      onChange={(e) => {
                        setEditingField({
                          ...editingField,
                          column_span: parseInt(e.target.value)
                        });
                      }}
                      style={{
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '0.95rem',
                        backgroundColor: 'white',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {(() => {
                        const maxColumns = layoutForm.sections[editingFieldSectionIndex]?.layout_columns || 2;
                        const options = [];
                        for (let i = 1; i <= maxColumns; i++) {
                          options.push(
                            <option key={i} value={i}>
                              {i} Column{i > 1 ? 's' : ''}
                            </option>
                          );
                        }
                        return options;
                      })()}
                    </Form.Select>
                    <Form.Text style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px' }}>
                      How many columns this field should span in the grid layout (max: {layoutForm.sections[editingFieldSectionIndex]?.layout_columns || 2}).
                    </Form.Text>
                  </Form.Group>
                </Form>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '16px 24px' }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowFieldConfigModal(false)}
            style={{ borderRadius: '8px', padding: '8px 20px' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (editingField && editingFieldSectionIndex !== null && editingFieldIndex !== null) {
                const updatedSections = [...layoutForm.sections];
                updatedSections[editingFieldSectionIndex].fields[editingFieldIndex] = editingField;
                setLayoutForm({ ...layoutForm, sections: updatedSections });
                setShowFieldConfigModal(false);
                setEditingField(null);
                setEditingFieldSectionIndex(null);
                setEditingFieldIndex(null);
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 20px',
              boxShadow: '0 2px 4px rgba(36, 58, 115, 0.3)'
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Layout Widget Manager Modal */}
      {showLayoutWidgetManager && selectedLayoutForWidgets && (() => {
        const layoutIdValue = selectedLayoutForWidgets.layout_id || selectedLayoutForWidgets.id;
        lmLog('📋 Rendering LayoutWidgetManager with:', {
          layoutId: layoutIdValue,
          selectedLayoutForWidgets,
          objectName
        });

        if (!layoutIdValue) {
          console.error('❌ Layout ID is missing in selectedLayoutForWidgets:', selectedLayoutForWidgets);
        }

        return (
          <LayoutWidgetManager
            objectName={objectName}
            layoutId={layoutIdValue}
            onClose={() => {
              setShowLayoutWidgetManager(false);
              setSelectedLayoutForWidgets(null);
            }}
            onSave={() => {
              fetchLayouts();
            }}
          />
        );
      })()}

      {/* Related List Manager Modal */}
      {showRelatedListManager && selectedLayoutForRelatedLists && (
        <RelatedListManager
          objectName={objectName}
          layoutId={selectedLayoutForRelatedLists.layout_id}
          onClose={() => {
            setShowRelatedListManager(false);
            setSelectedLayoutForRelatedLists(null);
          }}
          onSave={() => {
            fetchLayouts();
          }}
        />
      )}

      <ConfigureDetailLayoutActionsModal
        show={showActionsConfigModal}
        layout={selectedLayoutForActions}
        objectName={objectName}
        ctiEnabled={ctiEnabled}
        loading={loading}
        setGlobalLoading={setLoading}
        onHide={() => {
          setShowActionsConfigModal(false);
          setSelectedLayoutForActions(null);
        }}
        onSaved={() => {
          setSuccess('Actions configuration saved successfully!');
          fetchLayouts();
        }}
      />

      <Modal
        show={assignmentsModalOpen}
        onHide={() => setAssignmentsModalOpen(false)}
        size="xl"
        fullscreen="lg-down"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Layout &amp; record view access</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {assignmentsModalOpen ? (
            <LayoutRecordViewRoleBulkPage embedded fixedObjectName={objectName} />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => {
              setAssignmentsModalOpen(false);
              navigate(
                `/setup/layout-recordview-roles?object=${encodeURIComponent(objectName)}`
              );
            }}
          >
            Open in full page
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAssignmentsModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LayoutManager;
